// @ts-strict-ignore
import cloneDeep from 'lodash/cloneDeep';
import pick from 'lodash/pick';
import {AttrsSchema, Component} from 'panel';
import moment from 'moment';
import {produce} from 'immer';
import unzip from 'lodash/unzip';
import uniq from 'lodash/uniq';

import {openUserSidebar} from 'common/widgets/user-sidebar/utils';
import {extend, getAlphaLabelFromIndex} from 'common/util';
import {flattenSeriesToDepth, getDisplayOptions, mapObjectKeys} from 'common/report/insights/util';
import {
  ALL_MATH_TYPES,
  DownloadMode,
  ProjectFeatureFlag,
  PropertyType,
  ReportName,
  ResourceType,
  SPECIAL_EVENTS,
} from 'common/report/constants';
import {FilterOperator, NumberOperator, StringOperator} from 'common/widgets/property-filter-menu/models/types';
import {BehaviorShowClause, FormulaShowClause, ShowClauseClassType} from 'common/report/insights/models/show-clause';
import {FilterClause} from 'common/report/insights/models/filter-clause';
import {computeMetricClauseByKey, RenderLineChartTooltipParams} from 'common/report/insights/util/tooltip';
import {currentProjectStore} from 'common/report/stores/project-details';
import {findEventDefinitionByDisplayName} from 'common/report/stores/event-definitions';
import {useStore} from 'common/util/component-helpers';
import {
  getDownloadDisplayPreferences,
  getDownloadMode,
  getEventsReportUrl,
  getProjectId,
  getReportUrl,
  getWorkspaceId,
} from 'common/report/util';
import {getFilterSection, getPropertyFilters, getPropertyFiltersOperator} from 'common/report/insights/util/filters';
import {getEntries} from 'common/report/insights/util/events';
import {
  getBookmarkTimeClauseForDateRange,
  getDateRange,
  getTimeSection,
  timestampToXauDateRange,
} from 'common/report/insights/util/time';
import {createNestedCohort, mapForScdAndRevenue, translateMetricToCohort} from 'common/report/insights/util/translate';
import {
  BehaviorMetric,
  BehaviorShowClause as BehaviorShowClauseType,
  InsightsQueryResults,
} from 'common/report/insights/util/types';
import {
  AnalysisType,
  ChartType,
  ConversionWindowUnit,
  FunnelOrder,
  InsightsBookmarkParams,
  InsightsResourceType,
  MetricType,
  PRIMITIVE_MATH_TYPES,
  SubBehavior,
  ValueRepresentationType,
  XAU_MATH_TYPES,
} from 'common/types/reports/bookmark';
import {OTHER_SEGMENT_VALUE} from 'common/report/insights/util/constants';
import {SEGMENT_SEPARATOR} from 'common/report/funnels/models/constants';
import {formatDate, getCalendarDate} from 'common/util/date';
import {
  DateRangeType,
  OrderedStandardTimeUnits,
  TimeUnit,
  TimeUnitLabelTranslatable,
} from 'common/widgets/date-range-picker/models/types';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {
  CALENDAR_DATE_FORMAT,
  MPProperties,
  NON_NUMERIC_VALUES_SEGMENT,
  REMAINING_VALUES_SEGMENT,
  TIMESTAMP_FORMAT,
} from 'common/constants';
import {renameEvent} from 'common/report/util/events';
import {IronContextRegistry} from 'common/report/context-registry';
import 'common/report/insights/charts';
import {Report} from 'common/report/insights/models/report';
import {ProdTrackingEvent} from 'common/report/insights/tracking';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {openEventsSidebar} from 'common/widgets/events-sidebar';
import {GroupSection, ShowSection} from 'common/report/insights/models/section';
import {emitTrackEvent, propagateEvent} from 'common/util/dom';
import {pluralize} from 'common/util/string';
import {ColumnResizeEvent, ColumnWidthOverrides, SortChangeEvent} from 'common/components/data-table/types';
import {
  BehaviorDescriptor,
  createCohort as createFunnelCohort,
  getCohortText,
  getCohortTitle,
  getCohortWarning,
  resolveCompareStepsQueryToConcreteFunnelMultiMetric,
} from 'common/report/insights/charts/funnels-step-chart/cohorts';
import {BarType} from 'common/report/insights/charts/funnels-step-chart/types';
import {renamePropertyValue} from 'common/report/util/properties';
import {PopoverActionCallback, PopoverCallback, PopoverOption} from 'common/charts/types';
import {
  CalculationTypes,
  isMetricEntry,
  isSavedMetricEntry,
  QueryFormulaEntry,
} from 'common/query-builder/query-entry-section/types';
import {SectionItem} from 'common/components/items-menu/types';
import {GroupClause} from 'common/report/insights/models/group-clause';
import {Clause} from 'common/report/insights/models/clause';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {ReportSwitchEvent} from 'common/query-builder/query-builder/types';
import {StepPopoverAction, StepPopoverActionParams} from 'common/report/insights/util/chart';
import {
  findShowClauseIndex,
  hasEventComparison,
  hasGroupByDuration,
  isBehaviorShowClause,
} from 'common/report/insights/models/utils';
import {PAGE_DURATION} from 'common/query-builder/query-block/properties-menu/constants';
import {
  createDatetimeFilter,
  createFilterFromGroup,
  createNumberFilter,
  getFunnelSegmentNames,
  getNthTimeInPeriodFilter,
  getSelectedDateRange,
  getViewReplaysDisabledMessage,
  makeCohortFilter,
  shouldHideCohortFunctionality,
  sortFiltersForCohort,
} from 'common/report/insights/visualization/charts/chart-display/utils';
import {CohortType as FunnelCohortType} from 'common/report/funnels/models/types';
import {getResolvedFormulaEntry} from 'common/util/formulas';
import {CohortDetail} from 'common/widgets/create-cohort-button';
import {CurrentProject} from 'common/report/model-resources/projects';
import {convertSegmentLabelForCohort} from 'common/report/funnels/util';
import {RetentionBookmarkController} from 'common/report/retention/bookmark-controller';
import {CohortType as RetentionCohortType, Retention} from 'common/report/retention/models/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {RecordingPlayerModalAsync} from 'common/widgets/recording-player';
import {
  isTrackedReplayId,
  LATEST_REPLAYS_SHOW_CLAUSE,
  MAX_BY_TIME_MEASUREMENT,
  sessionRecordingQueryUtils,
} from 'common/widgets/recording-player/query-utils';
import {reporter} from 'common/report/error-reporting';
import {RecordingEventMetadata, RecordingEvents, RecordingPlayerSource} from 'common/widgets/recording-player/types';
import {getSessionRecordingBlockingInfo, isSessionRecordingEnabled} from 'common/report/util/feature-sampling';
import {SegmentLabelCreator} from 'common/widgets/user-sidebar/sidebar-user-list/types';
import {OnChangeControlGroupData} from 'common/report/insights/charts/funnels-step-chart';
import {LocalizationStore} from 'common/i18n/store';
import {NUMBER_OF_DAYS_IN_UNIT} from 'common/report/insights/models/time-clause';
import {OnPopoverActionProps} from 'common/report/insights/charts/insights-line-chart';
import {isXauMathType} from 'common/query-builder/query-builder/util';
import {DEFAULT_NUM_REPLAYS, queryUtils, REPLAY_ID_PROPERTY} from 'common/report/queries/query-api/session-replay';

import {convertSegmentForEventsInspector} from './utils';
import {template} from './template';

const XAU_MATH_TYPE_TIME_UNITS: Map<XAU_MATH_TYPES, TimeUnit> = new Map([
  [XAU_MATH_TYPES.MAU, TimeUnit.Month],
  [XAU_MATH_TYPES.DAU, TimeUnit.Day],
  [XAU_MATH_TYPES.WAU, TimeUnit.Week],
]);

interface ChartDisplayState {
  report: Report;
}

interface ChartDisplayAttrs {
  'resizing-layout'?: boolean;
  'can-use-view-n'?: boolean;
  'rca-source-id'?: number;
  'has-chart-date-range': boolean;
}

interface ChartDisplayProps {
  bookmarkParams: InsightsBookmarkParams;
  queryResults: InsightsQueryResults;
  dataGroups: DataGroup[];
  tableChartColumnWidthOverrides: ColumnWidthOverrides;
}

export type UpdateEvent = CustomEvent<{delta: Partial<InsightsBookmarkParams>}>;

export interface ChartDisplayJsxProps {
  attrs?: ChartDisplayAttrs;
  props: ChartDisplayProps;
  on: {
    update: (ev: UpdateEvent) => void;
    'export-csv': (ev: CustomEvent<void>) => void;
  };
}

export class ChartDisplay extends Component<ChartDisplayState, ChartDisplayAttrs, EmptyObj, void, IronContextRegistry> {
  private declare _bookmarkParams: InsightsBookmarkParams;
  private declare _queryResults: InsightsQueryResults;
  private declare _dataGroups: DataGroup[];
  private _tableChartColumnWidthOverrides: ColumnWidthOverrides = {};

  static get attrsSchema(): AttrsSchema<ChartDisplayAttrs> {
    return {
      ...super.attrsSchema,
      'resizing-layout': `boolean`,
      'can-use-view-n': `boolean`,
      'rca-source-id': `number`,
      'has-chart-date-range': `boolean`,
    };
  }

  set bookmarkParams(params: InsightsBookmarkParams) {
    this._bookmarkParams = params;
    this.update({report: Report.fromBookmarkData(params)});
  }

  get bookmarkParams() {
    return this._bookmarkParams;
  }

  set queryResults(results: InsightsQueryResults) {
    this._queryResults = results;
    this.update();
  }

  get queryResults() {
    return this._queryResults;
  }

  set dataGroups(groups: DataGroup[]) {
    this._dataGroups = groups;
    this.update();
  }

  get dataGroups() {
    return this._dataGroups;
  }

  set tableChartColumnWidthOverrides(overrides: ColumnWidthOverrides) {
    this._tableChartColumnWidthOverrides = overrides;
    this.update();
  }

  get tableChartColumnWidthOverrides() {
    return this._tableChartColumnWidthOverrides;
  }

  get t() {
    return this.getContext(`i18n`).t;
  }

  get print() {
    return this.getContext(`i18n`).print;
  }

  get config() {
    return {
      template,
      contexts: [
        `reportLayoutManager` as const,
        `urlManager` as const,
        `currentProject` as const,
        `metrics` as const,
        `behaviors` as const,
        `localStorageManager` as const,
        `i18n` as const,
      ],
      helpers: {
        extend,
        isDownloadMode: () => getDownloadMode(this.getContext(`urlManager`)) !== DownloadMode.None,
        enableSegmentsDrillDown: () => {
          const groupSection = this.state.report.sections.group;
          if (!groupSection.hasGroupByPropertyClause()) {
            return groupSection.hasGroupByCohortsClause();
          }
          const groupClauses = this.state.report.sections.group.clauses;
          if (this.hasMultipleListOfObjectsBreakdowns()) {
            return false;
          }
          return this.helpers.checkGroupClauseDrillDownValidity(groupClauses);
        },
        checkGroupClauseDrillDownValidity: (groupClauses: GroupClause[]): boolean => {
          return groupClauses.reduce((accumulator, groupClause) => {
            if (groupClause instanceof GroupSection) {
              return this.helpers.checkGroupClauseDrillDownValidity(groupClause.clauses);
            }
            const groupType = groupClause.typeCast || groupClause.propertyType;
            const isListOfObjects = groupType === `object` && groupClause.joinPropertyType === PropertyType.List;
            const isGroupByCohorts = groupClause.isGroupByCohorts();
            return (
              accumulator &&
              ([`datetime`, `string`, `number`, `boolean`, `list`].includes(groupType) ||
                isListOfObjects ||
                isGroupByCohorts)
            );
          }, true);
        },
        isBehavioralSegmentation: () => {
          const groupClauses = this.state.report.sections.group.clauses;
          return groupClauses.some((clause) => clause.behavior);
        },
        isNoDataProject: () => {
          return !(
            currentProjectStore.get(`hasIntegratedArb`, {defaultValue: true}) ||
            currentProjectStore.get(`hasIntegratedEngage`, {defaultValue: true})
          );
        },
        shouldOpenSidebar: () => currentProjectStore.value?.hasFeatureFlag(ProjectFeatureFlag.EventsSidebar),

        getActiveDataGroup: () => {
          const activeDataGroupIdStr = this.bookmarkParams.sections.globalDataGroupId;
          return this.dataGroups?.find((dg) => dg.groupID === activeDataGroupIdStr);
        },
        getActiveDataGroupDisplayName: () => {
          return this.helpers.getActiveDataGroup()?.displayName;
        },
        getChartLabel: () => {
          let label = `Value`;
          if (this.state.report.displayOptions.value === ValueRepresentationType.Relative) {
            label += ` (Percent of the Group)`;
          }
          return label;
        },
        getDisplayOptions: () => {
          const params = this.state.report.toBookmarkData();
          return getDisplayOptions(params.sections, {
            ...params.displayOptions,
            ...this.helpers.getDownloadDisplayPreferences(),
          });
        },
        getDownloadDisplayPreferences: () => getDownloadDisplayPreferences(this.getContext(`urlManager`)),
        getProjectId,
        getWorkspaceId,
        stringifyObjValues: (obj) => mapObjectKeys(obj, JSON.stringify),
        getTableChartData: () => {
          const metricClauseByKey = computeMetricClauseByKey({
            sectionsMetadata: this.queryResults.meta.report_sections,
            dataGroups: this.dataGroups,
            bookmarkParams: this.state.report.toBookmarkData(),
          });

          return {
            result: this.queryResults,
            meta: this.queryResults.meta,
            headers: this.queryResults.headers,
            metricClauseByKey,
          };
        },
        updateBarColumnWidths: (columnWidths) => {
          this.dispatchUpdate({columnWidths: {...this.state.report.columnWidths, bar: columnWidths}});
        },
        updateBarSortConfig: (barSortConfig) => {
          this.dispatchUpdate({
            sorting: {
              ...this.state.report.sorting,
              bar: barSortConfig,
            },
          });
        },

        getBasePopoverActions: (
          segmentPath: string[],
          {forSegmentColumn = false}: {forSegmentColumn?: boolean} = {},
        ): SectionItem[] => {
          const enableSegmentDrillDown =
            this.helpers.enableSegmentsDrillDown() &&
            !segmentPath.some((segment) => [REMAINING_VALUES_SEGMENT].includes(segment));
          let enableViewEvents = true;
          const clauses = this.getBehaviorClausesFromSegmentPath(segmentPath);
          if (
            clauses?.some(
              (clause) =>
                clause.behavior.type === MetricType.Funnel ||
                clause.behavior.type === MetricType.Retention ||
                clause.behavior.type === MetricType.RetentionFrequency,
            )
          ) {
            enableViewEvents = false;
          }
          let enableViewUsers = true;
          const {chartType} = this.helpers.getDisplayOptions();
          if (
            chartType === ChartType.Metric &&
            clauses?.some(
              (clause) =>
                clause.behavior.type === MetricType.Retention || clause.behavior.type === MetricType.RetentionFrequency,
            )
          ) {
            enableViewUsers = false;
          }
          const viewUsersDisabledReason = this.getViewUsersDisabledReason(segmentPath);
          const viewEventsDisabledReason = this.getViewEventsDisabledReason(segmentPath);

          const dataGroupName = pluralize(this.helpers.getActiveDataGroupDisplayName() || `Users`, true);
          const dataGroupLabel =
            dataGroupName === `Users`
              ? this.t(`chart-actions.view-users`)
              : this.t(`chart-actions.view-group`, {group: dataGroupName});
          const options = [
            enableSegmentDrillDown && {
              label: this.t(`chart-actions.filter`),
              icon: `filter`,
              value: PopoverOption.Filter,
            },
            enableSegmentDrillDown && {
              label: this.t(`chart-actions.exclude`),
              icon: `circle-drop`,
              value: PopoverOption.Exclude,
            },
            // view users for metric columns/paths
            enableViewUsers &&
              forSegmentColumn === false && {
                label: dataGroupLabel,
                icon: `team`,
                value: PopoverOption.ViewUsers,
                ...(viewUsersDisabledReason
                  ? {
                      isDisabled: true,
                      tooltip: {text: viewUsersDisabledReason},
                    }
                  : {}),
              },
            // view events for metric columns/paths
            enableViewEvents &&
              forSegmentColumn === false && {
                label: this.t(`chart-actions.view-events`),
                icon: `brand-liveview`,
                value: PopoverOption.ViewEvents,
                ...(viewEventsDisabledReason
                  ? {
                      isDisabled: true,
                      tooltip: {text: viewEventsDisabledReason},
                    }
                  : {}),
              },
          ];

          return options.filter(Boolean);
        },

        lineChartPopoverCallback: (
          metricKey,
          segments,
          {pointData}: {pointData: RenderLineChartTooltipParams},
        ): SectionItem[] => {
          const {timeUnit} = this.helpers.getDisplayOptions();
          const path = [metricKey, ...segments];
          const disableZoom = this.state.report.isCohortsOverTime() && timeUnit === TimeUnit.Day;
          const timestampMs = pointData.isComparisonItem
            ? pointData.timeComparisonStat?.highchartsTimestamp ?? pointData.liftComparisonStat.highchartsTimestamp
            : pointData.stat.highchartsTimestamp;
          const bucketOffsetMs =
            NUMBER_OF_DAYS_IN_UNIT[this.bookmarkParams.sections.time[0].unit] * 24 * 60 * 60 * 1000;

          const options = [
            timeUnit !== TimeUnit.Minute && {
              label: this.t(`chart-actions.zoom-in`),
              icon: `search`,
              value: PopoverOption.ZoomIn,
              isDisabled: disableZoom,
            },
            ...this.helpers.getBasePopoverActions(path),
            ...this.getViewReplaysPopoverActions(path, timestampMs + bucketOffsetMs),
            ...this.getRCAPopoverActions(this.attr(`rca-source-id`)),
          ];

          return options.filter(Boolean);
        },
        handleLineChartPopoverAction: (props: OnPopoverActionProps) => {
          const {pointData, action} = props.detail;
          const path = [pointData.metricClauseData.metricKey, ...pointData.segments];

          switch (action) {
            case PopoverOption.ViewUsers:
              this.handleLineChartViewUsers(pointData);
              break;
            case PopoverOption.ViewEvents:
              this.handleLineChartViewEvents(pointData);
              break;
            case PopoverOption.ZoomIn:
              this.handleLineChartZoomIn(pointData);
              break;
            case PopoverOption.Filter:
              this.handleTablePopoverFilter(path);
              break;
            case PopoverOption.Exclude:
              this.handleTablePopoverExclude(path);
              break;
            case PopoverOption.RCA:
              this.handleRCAClicked(pointData);
              break;
            case PopoverOption.ViewReplays:
              this.handleLineChartViewReplays(pointData);
              break;
            default:
              break;
          }
        },

        someSegmentHasMultiAttribution: (segments: string[]): boolean => {
          const clauses = this.getBehaviorClausesFromSegmentPath(segments);
          return clauses ? clauses.some((clause) => clause.measurement.multiAttribution) : false;
        },

        someSegmentHasSegmentMethod: (segmentPath: string[]) => {
          const clauses = this.getBehaviorClausesFromSegmentPath(segmentPath);
          return clauses?.some((clause) => clause.measurement.segmentMethod);
        },

        stepsChartPopoverAction: ({
          action,
          stepIndex,
          segmentProp,
          isDropOff,
          color,
          metricLabel,
          isComparison,
          comparisonDateRange,
        }: StepPopoverActionParams) => {
          if (action === StepPopoverAction.ViewUsers) {
            // in Step Chart, segmentProp is concetenated as a single string
            let segments = segmentProp.split(SEGMENT_SEPARATOR);
            segments = [metricLabel, ...segments];
            this.handleViewUsersForSegment(segments, {
              color,
              isDropOff,
              stepIndex,
              ...(isComparison ? {dateRange: comparisonDateRange} : {}),
            });
          } else if (action === StepPopoverAction.ViewInFlows) {
            this.handleViewInFlows({stepIndex});
          } else if (action === StepPopoverAction.ViewReplays) {
            // in Step Chart, segmentProp is concetenated as a single string
            let segments = segmentProp.split(SEGMENT_SEPARATOR);
            segments = [metricLabel, ...segments];

            this.handleViewReplaysForSegment(segments, {isDropOff, stepIndex});
          } else {
            throw new Error(`unsupported action`);
          }
        },

        metricChartPopoverCallback: (segmentName: string, segments: string[]): SectionItem[] => {
          if (segmentName === OTHER_SEGMENT_VALUE) {
            return [];
          }
          return this.helpers
            .getBasePopoverActions(segments)
            .filter((action) => [PopoverOption.ViewUsers, PopoverOption.ViewEvents].includes(action.value));
        },
        shouldDisplayTimeComparison: () => !!this.queryResults.time_comparison,
        dataTableSortChange: (options: SortChangeEvent[`detail`]) => {
          this.dispatchUpdate({
            sorting: {
              ...this.bookmarkParams.sorting,
              [ChartType.Table]: options.sorting,
            },
          });
        },
        dataTableColumnResize: (ev: ColumnResizeEvent) => {
          // we should use `this.dispatchUpdate` instead when columnWidths are added to bookmark for table chart
          propagateEvent(this, ev);
        },
        handleExportCsv: (ev) => {
          propagateEvent(this, ev);
        },
        handleChangeControlGroup: (data: OnChangeControlGroupData) => {
          this.dispatchUpdate({
            displayOptions: {
              ...this.helpers.getDisplayOptions(),
              statSigControl: data.props,
            },
          });
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    useStore(this, currentProjectStore);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  private getCurrentProject(): CurrentProject {
    return this.getContext(`currentProject`).get().value;
  }

  segmentPopoverCallback: PopoverCallback = (
    segmentName: string,
    segmentPath: string[],
    {forSegmentColumn, isTimeComparisonSegment} = {},
  ) => {
    if (segmentName === OTHER_SEGMENT_VALUE) {
      return [];
    }

    const latestDate = isTimeComparisonSegment
      ? this.queryResults.time_comparison.date_range.to_date
      : this.queryResults.date_range.to_date;
    return [
      ...this.helpers.getBasePopoverActions(segmentPath, {forSegmentColumn}),
      ...this.getViewReplaysPopoverActions(segmentPath, new Date(latestDate).getTime()),
    ];
  };

  createNonRemainingValueFiltersFromCustomGroups(customGroups, groupClause) {
    return customGroups.map((clause) => {
      if (clause.segmentLabel !== REMAINING_VALUES_SEGMENT) {
        return Object.assign({}, groupClause, {
          filterValue: clause.filterValue,
          filterOperator: clause.filterOperator,
        });
      }
    });
  }

  createGroupFromCustomGroups(customGroups, groupClause, segmentLabel) {
    let filteredGroupClause;

    customGroups.forEach((clause) => {
      if (clause.segmentLabel === segmentLabel) {
        filteredGroupClause = Object.assign({}, groupClause, {
          filterValue: clause.filterValue,
          filterOperator: clause.filterOperator,
        });
      }
    });

    return filteredGroupClause;
  }

  getFiltersFromPopoverSegments(
    segment,
    {makeNumericRangesExclusive = false}: {makeNumericRangesExclusive?: boolean} = {},
  ) {
    const filters = [];

    const filterSegments = segment.slice(1, segment.length);
    const groupClauses = this.state.report.sections.group.clauses;
    const metaGroupClauses = [...this.queryResults.meta.report_sections.group];
    const metaCohortsClauses = this.queryResults.meta.report_sections.cohorts || [];
    const timePeriodSettings = this.queryResults.meta.time_period_settings;
    // TODO: Remove this once report_sections contains cohorts in group
    if (metaCohortsClauses.length > 0) {
      const emptyGroupClause = {
        bookmark: {
          value: [],
          resourceType: InsightsResourceType.Events,
        },
      };
      metaGroupClauses.unshift(emptyGroupClause);
    }

    //Build filters for regular breakdowns and value comparisons
    filterSegments.forEach((segmentLabel, idx) => {
      const groupClause: GroupClause = groupClauses[idx];
      const metaGroupClause = metaGroupClauses[idx]?.bookmark;
      const isCustomStringGrouping = Array.isArray(metaGroupClause?.customGroups);
      // for the case of distribution query (histogram), groupClause could be nil
      if (groupClause?.isGroupByCohorts()) {
        const cohortFilter = this.createFilterFromCohort(segmentLabel, true);
        if (cohortFilter) {
          filters.push(cohortFilter);
        }
      } else if (metaGroupClause && Array.isArray(metaGroupClause)) {
        let filteredGroupClause;
        //Only the meta groupClause has the segmentLabel information but we want to use the bookmark groupClause to create the filter
        metaGroupClause.forEach((clause, clauseIndex) => {
          if (clause.segmentLabel === segmentLabel) {
            filteredGroupClause = groupClause[`clauses`][clauseIndex];
          }
        });
        filters.push(
          createFilterFromGroup(filteredGroupClause, filteredGroupClause.filterValue, true, timePeriodSettings),
        );
      } else if (isCustomStringGrouping && segmentLabel === REMAINING_VALUES_SEGMENT) {
        this.createNonRemainingValueFiltersFromCustomGroups(metaGroupClause.customGroups, groupClause).forEach(
          (groupFilter) => {
            filters.push(createFilterFromGroup(groupFilter, groupFilter.filterValue, false, timePeriodSettings));
          },
        );
        // Need to also filter out undefined values so we only have remaining values
        filters.push(createFilterFromGroup(groupClause, `undefined`, false, timePeriodSettings));
      } else if (isCustomStringGrouping && segmentLabel !== `undefined`) {
        // Handle the customGroups format for Customize Segments - do not handle the undefined case here
        const filteredGroupClause = this.createGroupFromCustomGroups(
          metaGroupClause.customGroups,
          groupClause,
          segmentLabel,
        );
        filters.push(
          createFilterFromGroup(filteredGroupClause, filteredGroupClause.filterValue, true, timePeriodSettings),
        );
      } else if (segmentLabel === NON_NUMERIC_VALUES_SEGMENT) {
        const numFilter = Object.assign({}, groupClause, {
          filterValue: groupClause.value,
          filterOperator: StringOperator.NotSet,
        });
        filters.push(createFilterFromGroup(numFilter, numFilter.filterValue, true, timePeriodSettings));
      } else if (metaGroupClause) {
        const filter = createFilterFromGroup(groupClause, filterSegments[idx], true, timePeriodSettings);
        // TODO: temp solution to keep SCD options here. they should be in the insight cohort utils.
        if (filter) {
          if (`scdEvent` in groupClause) {
            filter.scdEvent = groupClause.scdEvent;
            filter.scdOptions = groupClause.scdOptions;
          }
          // for numeric filters coming from a numerical range breakdown, the segment value is inclusive on the left and exclusive on the right, when
          // makeNumericRangesExclusive is set we transform this into an "at least" and "less than" filter to account for that range definition
          if (
            makeNumericRangesExclusive &&
            filter.filterType === PropertyType.Number &&
            filter.filterOperator === NumberOperator.Between
          ) {
            if (filter.scdOptions?.type !== `any`) {
              filters.push(
                {
                  ...filter,
                  filterOperator: NumberOperator.AtLeast,
                  filterValue: filter.filterValue[0],
                },
                {
                  ...filter,
                  filterOperator: NumberOperator.LessThan,
                  filterValue: filter.filterValue[1],
                },
              );
            } else {
              // We can't do this "less than or equal to && greater than"
              // translation for SCD "any" filters, because the "any" filter
              // will apply these conditions individually to different values
              // and match accounts or events that don't satisfy both of them.
              // Instead, we do this hack below.
              filters.push({
                ...filter,
                filterValue: [filter.filterValue[0], filter.filterValue[1] - 0.0000001],
              });
            }
          } else if (filter.filterJoinType === `list`) {
            // check for list filters of the same type, so they can be merged as a single list filter with multiple conditions
            let foundMatch = false;
            filters.forEach((listItem, filterIndex) => {
              if (this.isMatchingBasicListGroup(listItem, filter)) {
                filters[filterIndex].listItemFilters.push(filter.listItemFilters[0]);
                foundMatch = true;
              }
            });
            if (!foundMatch) {
              filters.push(filter);
            }
          } else {
            filters.push(filter);
          }
        }
      }
    });

    return filters;
  }

  isMatchingBasicListGroup(clause1, clause2) {
    const propType1 = clause1.typeCast || clause1.propertyType;
    const propType2 = clause2.typeCast || clause2.propertyType;

    if (
      // check if its a list type
      propType1 !== `object` ||
      clause1.filterJoinType !== `list` ||
      // check they are the same type
      clause1.filterJoinType !== clause2.filterJoinType ||
      clause1.value !== clause2.value ||
      clause1.filterType !== clause2.filterType ||
      propType1 !== propType2
    )
      return false;

    const propValue1 = clause1.listItemFilters[0].value;
    const propValue2 = clause2.listItemFilters[0].value;
    const computedListProps = [`$list_sum`, `$list_unique`, `$list_length`];
    return !(computedListProps.includes(propValue1) || computedListProps.includes(propValue2));
  }

  createFilterFromCohort(filterValue, includesFilterValue) {
    const cohortClauses = this.state.report.sections.group.getGroupByCohortsClauses();
    // The assumption here is no two cohorts in the same project share the same name.
    // It is true in today's production.
    const filteredCohortClauses = cohortClauses.filter(
      (clause) => clause.name === filterValue || `Not In ${clause.name}` === filterValue,
    );

    if (filteredCohortClauses.length > 0) {
      const cohortClause = filteredCohortClauses[0];
      const cohortDataset = cohortClause.dataset;
      const cohortProperties = pick(cohortClause, [`id`, `name`, `negated`, `raw_cohort`]);
      const negated = `Not In ${cohortClause.name}` === filterValue;
      let filterOperator: FilterOperator;
      if (includesFilterValue) {
        filterOperator = negated ? StringOperator.DoesNotContain : StringOperator.Contains;
      } else {
        filterOperator = negated ? StringOperator.Contains : StringOperator.DoesNotContain;
      }
      return {
        filterOperator,
        filterType: `list`,
        filterValue: [
          {
            cohort: {
              ...cohortProperties,
              negated: false,
            },
          },
        ],
        dataset: cohortDataset,
        resourceType: `events`,
        value: `$cohorts`,
      };
    }
    return null;
  }

  track(eventName: ProdTrackingEvent, props?: {[key: string]: any}) {
    emitTrackEvent(this, {eventName, props: props || {}});
  }

  getI18n(): LocalizationStore {
    return this.getContext(`i18n`);
  }

  toTrackingData() {
    return this.state.report.toTrackingData({
      i18n: this.getI18n(),
    });
  }

  trackSort(group, sortConfig, colIdx?: number) {
    const reportProperties = this.toTrackingData();
    const eventProperties = {};
    switch (group) {
      case `colSort`:
      case `left`:
        eventProperties[`sort by`] = sortConfig.colSortAttrs[colIdx].sortBy;
        eventProperties[`sort order`] = sortConfig.colSortAttrs[colIdx].sortOrder;
        eventProperties[`sort group`] = sortConfig.sortBy;
        eventProperties[`sort column index`] = colIdx;
        break;
      case `axisSort`:
      case `right`:
        eventProperties[`sort by`] = sortConfig.sortBy;
        eventProperties[`sort order`] = sortConfig.sortOrder;
        eventProperties[`sort group`] = `axis`;
        break;
    }
    this.track(ProdTrackingEvent.ChartSort, extend(reportProperties, eventProperties));
  }

  trackFilter(filter, projectId) {
    this.track(ProdTrackingEvent.PopoverAddFilter, {
      'Property name': filter.value,
      'Property data type': filter.propertyType,
      'Resource type': filter.resourceType,
      'Filter operator': filter.filterOperator,
      'Filter value': filter.filterValue,
      project_id: projectId,
      source: `insights-result`,
    });
  }

  getMatchingShowClauseIndexFromSegmentPath(path: string[]): number {
    // eslint-disable-next-line
    const {show: showMetadata} = this.queryResults?.meta.report_sections;

    if (!showMetadata || !path) {
      return null;
    }

    return findShowClauseIndex(showMetadata, path);
  }

  getClauseFromSegmentPath(path: string[]): ShowClauseClassType | null {
    const {clauses: showClauses} = this.state.report.sections.show;
    const clauseIdx = this.getMatchingShowClauseIndexFromSegmentPath(path);
    if (clauseIdx < 0) {
      return null;
    }

    return showClauses[clauseIdx];
  }

  getBehaviorClausesFromSegmentPath(path: string[]): BehaviorShowClause[] | null {
    const clause = this.getClauseFromSegmentPath(path);

    if (clause instanceof FormulaShowClause) {
      const formulaEntry = getResolvedFormulaEntry({
        formulaEntry: {event: clause},
        metricsResource: this.getContext(`metrics`),
        behaviorsResource: this.getContext(`behaviors`),
      });
      if (!formulaEntry) {
        return null;
      }
      const matchingMetricKeys = new Set(formulaEntry.event.definition.match(/[A-Z]{1,}/g));
      const relevantClauses = formulaEntry.event.referencedMetrics
        ? formulaEntry.event.referencedMetrics.map((clauseAttrs) => new BehaviorShowClause(clauseAttrs))
        : this.state.report.sections.show.clauses;
      const clauses = relevantClauses.filter((_clause, index) => matchingMetricKeys.has(getAlphaLabelFromIndex(index)));

      return clauses.length ? (clauses as BehaviorShowClause[]) : null;
    }

    return clause instanceof BehaviorShowClause ? [clause] : null;
  }

  isFormulaShowClause(path: string[]): boolean {
    return this.getClauseFromSegmentPath(path) instanceof FormulaShowClause;
  }

  getViewUsersDisabledReason(path: string[]) {
    const clauses = this.getBehaviorClausesFromSegmentPath(path);
    const {timeUnit} = this.helpers.getDisplayOptions();
    const isSubDayTimeUnit = timeUnit === TimeUnit.Hour || timeUnit === TimeUnit.Minute;

    if (clauses?.some((clause) => Boolean(clause.measurement.rolling)) && isSubDayTimeUnit) {
      return this.t(`chart-actions.disabled.time-granularity`, {
        timeUnit: this.print(TimeUnitLabelTranslatable[timeUnit](1)),
      });
    }

    if (!Array.isArray(clauses)) {
      return this.t(`chart-actions.disabled.clause-missing`);
    }

    if (clauses.some((clause) => clause.behavior.resourceType === InsightsResourceType.Cohorts)) {
      return this.t(`chart-actions.disabled.cohorts`);
    }

    if (
      clauses.some((clause) => {
        return (
          clause.behavior.type === MetricType.Retention ||
          clause.behavior.type === MetricType.Funnel ||
          clause.behavior.type === MetricType.RetentionFrequency
        );
      }) &&
      this.state.report.sections.group.clauses.some((group) => group.cohorts)
    ) {
      const QUERY_TYPE_MAPPER = {
        [MetricType.Retention]: this.t(`mixology.app.retention`),
        [MetricType.Funnel]: this.t(`mixology.app.funnels`),
        [MetricType.RetentionFrequency]: this.t(`retention.frequency`),
      };

      return this.t(`chart-actions.disabled.report-type`, {reportType: QUERY_TYPE_MAPPER[clauses[0].behavior.type]});
    }

    if (this.isFormulaShowClause(path)) {
      return this.t(`chart-actions.disabled.formulas`, {
        viewType: this.t(`chart-actions.view-users`),
      });
    }

    return null;
  }

  getSCDPropertiesDisabledReason(): string | null {
    const hasSCDProp = (clause) => Boolean(clause.scdEvent);
    if (
      this.state.report.sections.group.clauses.some(hasSCDProp) ||
      this.state.report.sections.filter.clauses.some(hasSCDProp)
    ) {
      return this.t(`chart-actions.disabled.history-tables`);
    }

    return null;
  }

  getViewEventsDisabledReason(path: string[]) {
    const clauses = this.getBehaviorClausesFromSegmentPath(path);
    const {timeUnit} = this.helpers.getDisplayOptions();
    const isSubDayTimeUnit = timeUnit === TimeUnit.Hour || timeUnit === TimeUnit.Minute;

    const scdDisabledReason = this.getSCDPropertiesDisabledReason();
    if (scdDisabledReason) {
      return scdDisabledReason;
    }

    if (!Array.isArray(clauses)) {
      return this.t(`chart-actions.disabled.clause-missing`);
    }

    if (clauses.some((clause) => Boolean(clause.measurement.rolling)) && isSubDayTimeUnit) {
      return this.t(`chart-actions.disabled.time-granularity`, {
        timeUnit: this.print(TimeUnitLabelTranslatable[timeUnit](1)),
      });
    }

    if (clauses.some((clause) => clause.behavior.resourceType === InsightsResourceType.Cohorts)) {
      return this.t(`chart-actions.disabled.cohorts`);
    }

    if (clauses.some((clause) => clause.behavior.resourceType === InsightsResourceType.People)) {
      return this.t(`chart-actions.disabled.profiles`);
    }

    if (clauses.some((clause) => (clause.behavior.filters || []).some((filter) => filter.value === `$cohorts`))) {
      return this.t(`chart-actions.disabled.cohort-filters`);
    }

    if (this.helpers.someSegmentHasSegmentMethod(path)) {
      return this.t(`chart-actions.disabled.count-users-once`);
    }

    if (this.helpers.someSegmentHasMultiAttribution(path)) {
      return this.t(`chart-actions.disabled.attribution`);
    }

    if (this.helpers.isBehavioralSegmentation()) {
      return this.t(`chart-actions.disabled.cohort-filters`);
    }

    if (this.isFormulaShowClause(path)) {
      return this.t(`chart-actions.disabled.formulas`, {
        viewType: this.t(`chart-actions.view-events`),
      });
    }

    return null;
  }

  addClause(type, clauseData) {
    const report = this.state.report;
    const section = report.sections[type];
    const clause = Clause.create(type, clauseData);
    const sections = report.sections.replaceSection(section.appendClause(clause));
    report.sections = sections;
    this.update({report});
    this.dispatchUpdate({sections: sections.toBookmarkData()});
  }

  handleSegmentPopoverAction: PopoverActionCallback = ({
    segment,
    action,
    isTimeComparisonSegment,
    color,
    columnIndex,
  }) => {
    switch (action) {
      case PopoverOption.Filter:
        this.handleTablePopoverFilter(segment, {columnIndex});
        break;
      case PopoverOption.Exclude:
        this.handleTablePopoverExclude(segment, {columnIndex});
        break;
      case PopoverOption.ViewUsers: {
        let dateRange;
        if (isTimeComparisonSegment) {
          const timeComparisonDateRange = this.queryResults.time_comparison?.date_range;
          const {from_date: from, to_date: to} = timeComparisonDateRange;
          dateRange = new DateRange({
            type: DateRangeType.Between,
            from: getCalendarDate(from),
            to: getCalendarDate(moment(to).endOf(`day`)),
          });
        }
        this.handleViewUsersForSegment(segment, {
          forTimeComparisonSegment: isTimeComparisonSegment,
          color,
          dateRange,
        });
        break;
      }
      case PopoverOption.ViewEvents:
        this.handleViewEventsForSegment(segment);
        break;
      case PopoverOption.ViewReplays:
        this.handleViewReplaysForSegment(segment, {isTimeComparisonSegment});
        break;
    }
  };

  handleTablePopoverFilter(segment: string[], {columnIndex = null}: {columnIndex?: number} = {}) {
    let filters = this.getFiltersFromPopoverSegments(segment);
    // Don't apply filters for segments to the right of where the user clicked.
    if (columnIndex !== null) {
      filters = filters.slice(0, columnIndex + 1);
    }

    filters.forEach((filterIn) => {
      this.addClause(FilterClause.TYPE, filterIn);
      this.trackFilter(filterIn, this.helpers.getProjectId());
    });
  }
  handleRCAClicked(pointData: RenderLineChartTooltipParams) {
    const clauseData = this.getLineChartPointClauseData(pointData);
    const url = `${getReportUrl(`boards`, {
      projectId: this.helpers.getProjectId(),
      workspaceId: this.helpers.getWorkspaceId(),
    })}#rca=${this.attr(`rca-source-id`)}&date=${clauseData.dateRange.to}&type=bookmark`;
    window.open(url);
  }

  handleTablePopoverExclude(segment, {columnIndex = null}: {columnIndex?: number} = {}) {
    let targetFilterValue;
    const groupClauses = this.state.report.sections.group.clauses;
    const groupCount = groupClauses.length;

    /** when a columnIndex is set, we want to exclude a specific value in a table, instead of just excluding the leaf segment */
    if (columnIndex !== null) {
      targetFilterValue = segment[columnIndex + 1]; // account for the metric segmentation path
    } else {
      //Bar charts will have "all" as the last segment value so we want to avoid grabbing that and instead grab the value just prior to it
      targetFilterValue =
        segment[segment.length - 1] === `all` ? segment[segment.length - 2] : segment[segment.length - 1];
    }

    const columnTarget = columnIndex ?? groupCount - 1;
    const groupClause = groupClauses[columnTarget];
    const timePeriodSettings = this.queryResults.meta.time_period_settings;
    //We need to use metaGroupClause because it has the segmentLabel attribute that allows us to uniquely identify the relevant groupClause
    // TODO: Fix metaGroupClause TypeError
    const metaGroupClause = this.queryResults.meta.report_sections.group[columnTarget].bookmark;
    const isCustomStringGrouping = Array.isArray(metaGroupClause.customGroups);
    const isCustomNumericGrouping = !!metaGroupClause.customBucket?.groups;

    if (groupCount > 0) {
      if (metaGroupClause && isCustomStringGrouping && targetFilterValue !== `undefined`) {
        // Handle the customGroups format for Customize Segments - do not handle the undefined case here
        const filteredGroupClause = this.createGroupFromCustomGroups(
          metaGroupClause.customGroups,
          groupClause,
          targetFilterValue,
        );
        this.addClause(
          FilterClause.TYPE,
          createFilterFromGroup(filteredGroupClause, filteredGroupClause.filterValue, false, timePeriodSettings),
        );
        this.trackFilter(filteredGroupClause, this.helpers.getProjectId());
      } else if (metaGroupClause && isCustomNumericGrouping && targetFilterValue === NON_NUMERIC_VALUES_SEGMENT) {
        const numFilter = Object.assign({}, groupClause, {
          filterValue: groupClause.value,
          filterOperator: StringOperator.Set,
        });
        this.addClause(
          FilterClause.TYPE,
          createFilterFromGroup(numFilter, numFilter.filterValue, true, timePeriodSettings),
        );
        this.trackFilter(numFilter, this.helpers.getProjectId());
      } else if (metaGroupClause && groupClause.isGroupByCohorts()) {
        const filterOut = this.createFilterFromCohort(targetFilterValue, false);
        if (filterOut) {
          this.addClause(FilterClause.TYPE, filterOut);
        }
      } else {
        const filterOut = createFilterFromGroup(groupClause, targetFilterValue, false, timePeriodSettings);
        if (filterOut) {
          this.addClause(FilterClause.TYPE, filterOut);
          this.trackFilter(filterOut, this.helpers.getProjectId());
        }
      }
    }
  }

  getLineChartPointClauseData(pointData: RenderLineChartTooltipParams, hasAnyOrderFunnelsQuery?: boolean) {
    const timestamp = pointData.isComparisonItem
      ? // it's either time comparison or value comparison
        (pointData.liftComparisonStat || pointData.timeComparisonStat).highchartsTimestamp
      : pointData.stat.highchartsTimestamp;
    const clause = pointData.metricClauseData;
    let calculationType;
    // TODO: Does this format make sense? Should these be clause.measurement.rolling/cumulative?
    if ((clause as any).rolling) {
      calculationType = CalculationTypes.Rolling;
    } else if ((clause as any).measurement.cumulative) {
      calculationType = CalculationTypes.Cumulative;
    }
    const timePeriodSettings = this.queryResults?.meta.time_period_settings;
    const timeWindowUnit = pointData.timeUnit;
    const groupProperties = {unit: timeWindowUnit};

    const dateRangeStr = formatDate(timestamp, {unit: timeWindowUnit});

    let subDayFilters = [];

    if (`math` in clause) {
      if (clause.math === ALL_MATH_TYPES.CUMULATIVE_UNIQUE) {
        return {
          calculationType,
          subDayFilters,
          dateRange: new DateRange({
            type: DateRangeType.Between,
            from: getCalendarDate(pointData.stat.dateRange.fromDate),
            to: dateRangeStr,
          }),
        };
      }

      if (XAU_MATH_TYPE_TIME_UNITS.has(clause.math as any)) {
        const {fromDate, toDate} = timestampToXauDateRange(timestamp, {
          unit: pointData.timeUnit,
          operator: clause.math,
          isLastInterval: pointData.isLastInterval,
          lastIntervalToDate: pointData.stat.dateRange.toDate,
        });

        return {
          calculationType,
          subDayFilters,
          dateRange: new DateRange({
            type: DateRangeType.Between,
            from: getCalendarDate(fromDate ?? toDate),
            to: getCalendarDate(toDate),
          }),
        };
      }
    }

    const dataset = `dataset` in clause ? clause.dataset : ``;
    const dateFilter = createDatetimeFilter(dataset, groupProperties, dateRangeStr, timePeriodSettings, true);

    let dateFromToRange;
    if (timeWindowUnit === TimeUnit.Hour && Array.isArray(dateFilter.filterValue)) {
      // createDatetimeFilter adds an hour onto the time from dateRangeStr which will give
      // us a total of 2 hours instead of 1, so here we subtract 1 minute to get 1 full hour.
      dateFromToRange = {
        from: dateFilter.filterValue[0],
        to: moment(dateFilter.filterValue[1], TIMESTAMP_FORMAT).subtract(1, `minute`).format(TIMESTAMP_FORMAT),
      };
    } else if (timeWindowUnit === TimeUnit.Minute && Array.isArray(dateFilter.filterValue)) {
      // createDatetimeFilter adds a minute onto the time from dateRangeStr which will give
      // us a total of 2 minutes instead of 1, so here we subtract 1 second to get 1 full minute.
      dateFromToRange = {
        from: dateFilter.filterValue[0],
        to: moment(dateFilter.filterValue[1], TIMESTAMP_FORMAT).subtract(1, `second`).format(TIMESTAMP_FORMAT),
      };
    } else if (Array.isArray(dateFilter.filterValue)) {
      dateFromToRange = {from: dateFilter.filterValue[0], to: dateFilter.filterValue[1]};
    } else {
      dateFromToRange = {from: dateFilter.filterValue, to: dateFilter.filterValue};
    }

    let dateRange: DateRange;
    const isSubDayTimeUnit = pointData.timeUnit === TimeUnit.Hour || pointData.timeUnit === TimeUnit.Minute;
    if (isSubDayTimeUnit && hasAnyOrderFunnelsQuery) {
      // For minute/hour line charts on any-order funnels, we need to filter on `hour_of_day`
      subDayFilters = [
        new PropertyFilter({
          filterOperator: DateRangeType.Between,
          filterValue: new DateRange({
            type: DateRangeType.Between,
            from: moment(dateFromToRange.from).format(TIMESTAMP_FORMAT),
            to: moment(dateFromToRange.to).format(TIMESTAMP_FORMAT),
          }),
          propertyDefaultType: PropertyType.Datetime,
          propertyName: MPProperties.$Time,
          propertyType: PropertyType.Datetime,
          resourceType: ResourceType.Event,
        }),
      ];

      // Round dateRange from/to to day.
      dateRange = new DateRange({
        type: DateRangeType.Between,
        ...{
          from: moment(dateFromToRange.from).format(CALENDAR_DATE_FORMAT),
          to: moment(dateFromToRange.to).format(CALENDAR_DATE_FORMAT),
        },
      });
    } else {
      dateRange = new DateRange({
        type: DateRangeType.Between,
        ...dateFromToRange,
      });
    }

    return {calculationType, subDayFilters, dateRange, timestamp};
  }

  handleLineChartZoomIn(pointData: RenderLineChartTooltipParams) {
    const dateRange = this.getLineChartViewEntitiesDateRange(pointData);
    const {subDayFilters} = this.getLineChartPointClauseData(
      pointData,
      this.state.report.sections.show.hasAnyOrderFunnelsQuery(),
    );
    const selectedTimeUnit = this.state.report.sections.time.clauses[0].unit;
    const selectedFilters = this.state.report.sections.filter;
    const updatedTimeUnit =
      OrderedStandardTimeUnits[OrderedStandardTimeUnits.findIndex((unit) => unit === selectedTimeUnit) - 1];

    let showSection = this.state.report.sections.show;
    if (this.state.report.sections.show.hasFunnelsQuery() && subDayFilters.length > 0) {
      // If we added a filter and there's a funnel query then we must update the show clause.
      showSection = this.getShowSectionForFunnelsFilters();
    }
    const sections = this.state.report.sections
      .replaceSection(getTimeSection(dateRange, {overrideTimeUnit: updatedTimeUnit}))
      .replaceSection(
        getFilterSection(
          [...getPropertyFilters(selectedFilters.clauses), ...subDayFilters],
          getPropertyFiltersOperator(selectedFilters.determiner),
        ),
      )
      .replaceSection(showSection)
      .toBookmarkData();

    this.dispatchUpdate({sections});
    this.track(ProdTrackingEvent.PopoverZoomIn, this.toTrackingData());
  }

  getLineChartViewEntitiesDateRange(pointData: RenderLineChartTooltipParams): DateRange {
    const displayOptions = this.state.report.displayOptions;
    const {dateRange, calculationType} = this.getLineChartPointClauseData(pointData);

    /** cumulative view specifies users up to the point in the chart that was clicked */
    if (displayOptions.analysis === AnalysisType.Cumulative || calculationType === CalculationTypes.Cumulative) {
      dateRange.from = getCalendarDate(this.queryResults.date_range.from_date);
    } else if (displayOptions.analysis === AnalysisType.Rolling || calculationType === CalculationTypes.Rolling) {
      const windowSize = displayOptions.rollingWindowSize;
      dateRange.from = getCalendarDate(moment(dateRange.from as string).subtract(windowSize - 1));
    }

    // Make sure the date range is within the date range of the chart
    const chartDateRange = pointData?.stat?.dateRange;
    const hasChartDateRange =
      this.attr(`has-chart-date-range`) && chartDateRange && `fromDate` in chartDateRange && `toDate` in chartDateRange;
    // DAU/WAU/MAU data points query a rolling date range window that can go past the chart date range
    const isXau = `math` in pointData.metricClauseData ? isXauMathType(pointData.metricClauseData.math) : false;
    if (hasChartDateRange && !isXau) {
      return dateRange.clamped([chartDateRange.fromDate, chartDateRange.toDate]);
    }

    return dateRange;
  }

  handleLineChartViewUsers(pointData: RenderLineChartTooltipParams) {
    const path = [pointData.metricClauseData.metricKey, ...pointData.segments];
    const clauseData = this.getLineChartPointClauseData(pointData);
    const dateRange = this.getLineChartViewEntitiesDateRange(pointData);

    this.handleViewUsersForSegment(path, {
      dateRange,
      timestamp: clauseData.timestamp,
      forTimeComparisonSegment: pointData.isComparisonItem,
      color: pointData.titleColor,
    });
  }

  handleLineChartViewReplays(pointData: RenderLineChartTooltipParams) {
    const path = [pointData.metricClauseData.metricKey, ...pointData.segments];
    const {subDayFilters} = this.getLineChartPointClauseData(
      pointData,
      this.state.report.sections.show.hasAnyOrderFunnelsQuery(),
    );
    const dateRange = this.getLineChartViewEntitiesDateRange(pointData);

    this.handleViewReplaysForSegment(path, {dateRange, isDropOff: false, subDayFilters});
  }

  getSegmentIndexPropertyName(index: number) {
    const groupBys = this.state.report.sections.group.clauses;
    const group = groupBys[index];

    // for the case of distribution query (histogram), group could be nil
    if (!group || group?.behavior) {
      return null;
    }

    return group.value;
  }

  createFunnelBehaviorDescriptor({
    multiMetricBookmark,
    funnelShowClause,
    groupByValue,
    isDropOff,
    stepIndex,
  }: {
    multiMetricBookmark: InsightsBookmarkParams;
    funnelShowClause: BehaviorShowClause;
    groupByValue: string[];
    isDropOff: boolean;
    stepIndex: number;
  }) {
    // Determine which `step` we are referencing re: View Users.
    // Top of the funnel = number of unique funnel entries over time
    // Bottom of the funnel = number of ultimate funnels over time (All Steps or Last Steps)
    const funnelOnlyMultiMetricBookmark = cloneDeep(multiMetricBookmark);
    funnelOnlyMultiMetricBookmark[`sections`][`show`] = [funnelShowClause];
    const steps = funnelShowClause.behavior.behaviors;
    // in case of `All Steps` we get `stepIndex = null`
    // step index is passed in only for Funnel Steps View
    stepIndex =
      stepIndex !== null
        ? stepIndex
        : (funnelOnlyMultiMetricBookmark[`sections`][`show`][0][`measurement`][`stepIndex`]
            ? funnelOnlyMultiMetricBookmark[`sections`][`show`][0][`measurement`][`stepIndex`] + 1
            : steps.length) - 1;

    // If `chartType = line` then Trends, if `chartType = funnel-steps` then Step.
    const chartType = funnelOnlyMultiMetricBookmark[`displayOptions`][`chartType`];

    let behaviorDescriptor = null;
    switch (chartType) {
      case ChartType.Column:
      case ChartType.Line:
      case ChartType.Bar:
      case ChartType.Table:
      case ChartType.Metric:
        behaviorDescriptor = {
          index: stepIndex,
          selectedBarType: BarType.CONVERTED,
          groupByValue,
          type: `converted`,
        } as BehaviorDescriptor;
        break;
      case ChartType.FunnelSteps:
        behaviorDescriptor = {
          index: stepIndex,
          selectedBarType: BarType.CONVERTED,
          groupByValue,
          type: isDropOff ? FunnelCohortType.STEP_DROP_OFF : `converted`,
        } as BehaviorDescriptor;
        break;
      default:
        console.error(`Invalid chart type for View Users`);
        return null;
    }

    return behaviorDescriptor;
  }

  handleShowClauseForViewUsersWithPageDuration(showClauses: BehaviorShowClauseType[]): BehaviorShowClause[] {
    // If we have a duration group by, we need to transform events show clauses to funnel based show clauses as
    // this is what query api does for the main query. We cant let query api handle this logic as we want to make sure
    // we want the view users handling for funnels, for e.g. expand should create a place holder cohort in the users page
    // instead of trying to represent duration as a prop in that page which is not supported.
    const transformedShowClauses = [];
    for (const showClause of showClauses) {
      if (isBehaviorShowClause(showClause) && showClause.behavior.type === MetricType.Event) {
        transformedShowClauses.push(
          new BehaviorShowClause({
            behavior: {
              type: MetricType.Funnel,
              resourceType: InsightsResourceType.Events,
              dataGroupId: showClause.behavior.dataGroupId,
              dataset: showClause.behavior.dataset,
              filters: [],
              conversionWindowDuration: 1,
              conversionWindowUnit: ConversionWindowUnit.Session,
              funnelOrder: FunnelOrder.Loose,
              exclusions: [],
              aggregateBy: [],
              behaviors: [
                {
                  id: showClause.behavior.id,
                  name: showClause.behavior.name,
                  type: showClause.behavior.type,
                  filters: showClause.behavior.filters,
                  filtersDeterminer: showClause.behavior.filtersDeterminer,
                  funnelOrder: FunnelOrder.Loose,
                },
                {
                  id: showClause.behavior.id,
                  name: showClause.behavior.name,
                  type: showClause.behavior.type,
                  filters: showClause.behavior.filters,
                  filtersDeterminer: showClause.behavior.filtersDeterminer,
                  funnelOrder: FunnelOrder.Loose,
                },
              ],
            },
            measurement: {
              stepIndex: showClause.measurement.property?.name === PAGE_DURATION.value ? 1 : 0,
              ...showClause.measurement,
            },
          }),
        );
      } else {
        transformedShowClauses.push(showClause);
      }
    }

    return transformedShowClauses;
  }

  getFunnelCohort({segment, isDropOff, stepIndex, timestamp = null, dateRange = null}) {
    const showClauses = this.getBehaviorClausesFromSegmentPath(segment);
    if (showClauses === null) {
      throw new Error(`No show clause found for segment: ${segment.join(` / `)}`);
    }

    const currentProject = this.getCurrentProject();
    const funnelShowClause = showClauses[0];

    const behaviorDescriptor = this.createFunnelBehaviorDescriptor({
      multiMetricBookmark: this.bookmarkParams,
      funnelShowClause,
      groupByValue: segment,
      isDropOff,
      stepIndex,
    });
    const segmentNames = getFunnelSegmentNames({
      behaviors: funnelShowClause.behavior.behaviors,
      group: this.bookmarkParams.sections.group,
    });
    const selectedDateRange = timestamp
      ? getSelectedDateRange({
          date: timestamp,
          unit: this.state.report.sections.time.clauses[0].unit,
          quarterStartMonth: currentProject.timePeriodSettings?.quarterStartMonth || 0,
        })
      : dateRange;

    const funnelBookmark = {
      ...this.bookmarkParams,
      sections: {
        ...this.bookmarkParams.sections,
        show: [funnelShowClause],
      },
    };

    return createFunnelCohort({
      name: getCohortTitle({
        index: behaviorDescriptor.index,
        type: isDropOff ? FunnelCohortType.STEP_DROP_OFF : FunnelCohortType.CONVERTED,
        reportName: `PLACEHOLDER`,
        ...(segmentNames.length > 0
          ? {segment: convertSegmentLabelForCohort(segment.slice(1).join(` / `), segmentNames)}
          : {}),
        dataGroup: this.helpers.getActiveDataGroup(),
      }),
      description: getCohortText({
        cohortType: isDropOff ? FunnelCohortType.STEP_DROP_OFF : FunnelCohortType.CONVERTED,
        stepNames: funnelShowClause.behavior.behaviors.map((beh) => beh.name),
        exclusionSteps: funnelShowClause.behavior.exclusions,
        reportName: `PLACEHOLDER`,
        dateRange: getDateRange(this.state.report.sections.time.clauses),
        conversionRange: null,
        segment:
          segmentNames.length > 0 ? convertSegmentLabelForCohort(segment.slice(1).join(` / `), segmentNames) : null,
        dataGroup: this.helpers.getActiveDataGroup(),
      }),
      behaviorDescriptor,
      multiMetricBookmark: funnelBookmark,
      selectedDateRange,
    });
  }

  handleViewUsersForSegment(
    segment: string[],
    {
      subDayFilters = [],
      dateRange = null,
      timestamp = null,
      forTimeComparisonSegment = false,
      color = ``,
      isDropOff = false,
      stepIndex = null,
    } = {},
  ) {
    let showClauses = this.getBehaviorClausesFromSegmentPath(segment);
    if (showClauses === null) {
      reporter.error(`View users: No show clause found for segment`, segment);
      return;
    }
    const isMultiMetricBookmark = showClauses.every((bm) => `measurement` in bm && `behavior` in bm);
    const dataGroup = this.helpers.getActiveDataGroup();
    const dataGroupName = this.helpers.getActiveDataGroupDisplayName();
    const currentProject = this.getCurrentProject();

    if (isMultiMetricBookmark && hasGroupByDuration(this.bookmarkParams.sections.group)) {
      showClauses = this.handleShowClauseForViewUsersWithPageDuration(showClauses);
    }

    let cohortDetail: CohortDetail;
    let segmentLabelCreator: SegmentLabelCreator;
    if (isMultiMetricBookmark && showClauses[0].behavior.type === MetricType.Funnel) {
      const funnelShowClause = showClauses[0];
      const projectAsOfDate = currentProject?.projectAsOfDate;

      const cohort = this.getFunnelCohort({segment, isDropOff, dateRange, timestamp, stepIndex});
      cohortDetail = {
        label: this.getContext(`i18n`).t(`view-users.create-cohort`),
        type: isDropOff ? FunnelCohortType.STEP_DROP_OFF : FunnelCohortType.CONVERTED,
        warning: getCohortWarning(
          getDateRange(this.state.report.sections.time.clauses),
          {
            unit: funnelShowClause.behavior.conversionWindowUnit,
            value: funnelShowClause.behavior.conversionWindowDuration,
          },
          BarType.CONVERTED,
          dataGroup,
          projectAsOfDate,
        ),
        cohort,
      };
      segmentLabelCreator = isDropOff
        ? ({context, count, group}) =>
            ({t}) =>
              t(`view-users.who-dropped-off`, {context, count, group})
        : ({context, count, group}) =>
            ({t}) =>
              t(`view-users.who-converted`, {context, count, group});
    } else if (
      isMultiMetricBookmark &&
      (showClauses[0].behavior.type === MetricType.Retention ||
        showClauses[0].behavior.type === MetricType.RetentionFrequency)
    ) {
      const bookmarkController = new RetentionBookmarkController(
        this.bookmarkParams,
        this.queryResults as unknown as InsightsQueryResults<Retention>,
      );

      let matchingTrendIndex = 0;
      let matchingTrend = null;
      let absoluteValue = null;
      let index = 0;
      const timeSeries = bookmarkController.getTimeSeries(segment);
      const selectedDate = formatDate(timestamp, {iso: true, utc: true});
      for (const [date, value] of Object.entries(timeSeries)) {
        const formattedDate = formatDate(new Date(date), {iso: true, utc: true});
        if (formattedDate === selectedDate) {
          matchingTrendIndex = index;
          matchingTrend = formattedDate;
          absoluteValue = value;
          break;
        }
        index++;
      }
      const matchingShowClauseIndex = this.getMatchingShowClauseIndexFromSegmentPath(segment);
      const retentionBucketIndex = bookmarkController.getRetentionBucketIndex(matchingShowClauseIndex);
      const incompletBuckets = bookmarkController.getIncompletBuckets();
      const completeDate =
        incompletBuckets && incompletBuckets[matchingTrend] && incompletBuckets[matchingTrend][retentionBucketIndex];

      const retentionPopover = bookmarkController.createPopover({
        matchingShowClauseIndex,
        projectTimezone: currentProject?.timezone,
        colIdx: matchingTrendIndex,
        rowIdx: retentionBucketIndex,
        groupedRowIdx: matchingTrendIndex,
        absoluteValue,
        absoluteDropOffValue: null,
        cohortSize: null,
        completeDate,
        enteredValue: bookmarkController.isSegmented() ? segment.slice(1).join(` / `) : formatDate(timestamp),
        enteredDate: new Date(timestamp),
        incomplete: !!completeDate,
        isAggregated: bookmarkController.getSegments().length > 0,
        i18n: this.getContext(`i18n`),
      });
      cohortDetail = retentionPopover.getCohortDetails()[0];
      const type = isDropOff
        ? RetentionCohortType.SpecificIntervalDropOff
        : RetentionCohortType.SpecificIntervalRetained;
      segmentLabelCreator = retentionPopover.getSegmentLabelCreator(type);
    } else {
      const cohort = createNestedCohort(
        showClauses.map((clause, index) =>
          this.getCohortForSegmentShowClause(clause, segment, {
            index,
            subDayFilters,
            dateRange,
            forTimeComparisonSegment,
          }),
        ),
      );
      if (this.bookmarkParams.sections.globalDataGroupId) {
        cohort.dataGroupId = this.bookmarkParams.sections.globalDataGroupId;
      }
      // TODO: hansli proper name & description for the cohort
      cohort.name = `[Untitled] - ${segment[0]}`;
      cohort.description = `Insight Report: Untitled event: ${segment[0]}`;
      cohortDetail = {
        label: `Create Cohort`,
        type: `insights`,
        warning: ``,
        cohort,
      };
    }

    const segments = [
      segment[0],
      ...segment
        .slice(1)
        .map((value, index) =>
          renamePropertyValue(
            value,
            this.getSegmentIndexPropertyName(index),
            this.bookmarkParams.sections.group[index],
          ),
        ),
    ];
    const params = {
      include_all_users: !this.state.report.sections.show.isPeopleOnlyQuery(),
      serialized_cohort: encodeURIComponent(JSON.stringify(cohortDetail.cohort.serialize())),
      query_source: `View Users`,
      ...(cohortDetail.cohort.dataGroupId
        ? {
            data_group_id: cohortDetail.cohort.dataGroupId,
          }
        : {}),
    };

    const trackingData = Object.assign(this.toTrackingData(), {
      'View Users: selected Show Clause': showClauses[0].toBookmarkData(),
      'View Users: selected Segments': segment.slice(1).map((s, idx) => ({
        segmentValue: s,
        segmentGroup: this.state.report.sections.group.clauses[idx],
      })),
    });
    this.track(ProdTrackingEvent.PopoverViewUsers, trackingData);

    openUserSidebar({
      includeAllUsers: params.include_all_users,
      hideCohortFunctionality: shouldHideCohortFunctionality(cohortDetail.cohort),
      reportName: ReportName.Insights,
      segments,
      ...(dataGroupName
        ? {
            dataGroupName,
          }
        : {}),
      ...(dateRange
        ? {
            fromDate: dateRange.from,
            toDate: dateRange.to,
          }
        : {}),
      color,
      cohort: cohortDetail.cohort,
      cohortDetails: [cohortDetail],
      createSegmentLabel: segmentLabelCreator,
    })
      .then(() => {
        this.getContext(`reportLayoutManager`).updateIsUserSidebarOpen(true);
      })
      .catch(() => {
        this.getContext(`reportLayoutManager`).updateIsUserSidebarOpen(false);
      });
  }

  getRCAPopoverActions(rcaSourceId: number): SectionItem[] {
    const disabledAttrs: Partial<SectionItem> = !rcaSourceId
      ? {isDisabled: true, tooltip: {text: this.t(`chart-actions.disabled.save-for-rca`)}}
      : {};

    return [
      {
        label: this.t(`chart-actions.root-cause-analysis`),
        icon: `analysis`,
        value: PopoverOption.RCA,
        ...disabledAttrs,
      },
    ];
  }

  getViewReplaysPopoverActions(segmentPath: string[], timestampMs?: number): SectionItem[] {
    if (!isSessionRecordingEnabled(this.getCurrentProject())) {
      return [];
    }

    const showClauses = this.getBehaviorClausesFromSegmentPath(segmentPath);

    if (
      showClauses === null ||
      ![MetricType.Funnel, MetricType.Event, MetricType.CustomEvent, MetricType.Cohort, MetricType.People].includes(
        showClauses[0].behavior.type,
      )
    ) {
      return [];
    }

    if (!timestampMs) {
      timestampMs = new Date(this.queryResults.date_range.to_date).getTime();
    }

    const disabledReason = sessionRecordingQueryUtils.getReportReplaysDisabledReason({
      replayTimestampMs: timestampMs,
      showClauses,
      currentProject: this.getCurrentProject(),
    });
    const disabledMessage = getViewReplaysDisabledMessage(disabledReason, this.t);
    const disabledAttrs: Partial<SectionItem> = disabledMessage
      ? {isDisabled: true, tooltip: {text: disabledMessage}}
      : {};

    return [
      {
        label: this.t(`chart-actions.view-replays`),
        icon: `replay`,
        value: PopoverOption.ViewReplays,
        ...disabledAttrs,
      },
    ];
  }

  handleViewReplaysForSegment(
    segment: string[],
    {
      isDropOff = false,
      isTimeComparisonSegment = false,
      stepIndex = null,
      dateRange = null,
      subDayFilters = null,
    }: {
      isDropOff?: boolean;
      stepIndex?: number;
      dateRange?: DateRange;
      subDayFilters?: PropertyFilter[];
      isTimeComparisonSegment?: boolean;
    } = {},
  ) {
    const showClauses = this.getBehaviorClausesFromSegmentPath(segment);
    if (showClauses === null) {
      reporter.error(`View replays: No show clause found for segment`, segment);
      return;
    }

    const isMultiMetricBookmark = showClauses.every((bm) => `measurement` in bm && `behavior` in bm);
    if (!isMultiMetricBookmark) {
      // this should never happen, bookmarks will be migrated to new format
      throw new Error(`Viewing replays is not supported for this bookmark`);
    }

    const viewReplaySections = sessionRecordingQueryUtils.getSectionsForReportEntrypoint(
      this.getCurrentProject(),
      dateRange ?? new DateRange(this.bookmarkParams.sections.time[0]),
    );

    let bookmarkWithReplaySegmentation: InsightsBookmarkParams = produce(this.bookmarkParams, (draft) => {
      if (subDayFilters) {
        const filterClauses = subDayFilters.map((filter) => FilterClause.fromPropertyFilter(filter).toBookmarkData());
        draft.sections.filter = [...draft.sections.filter, ...filterClauses];
      }

      if (dateRange) {
        draft.sections.time = [getBookmarkTimeClauseForDateRange(dateRange)];
      } else if (isTimeComparisonSegment) {
        draft.sections.time = [
          {
            unit: draft.sections.time[0].unit,
            dateRangeType: DateRangeType.Between,
            value: [
              getCalendarDate(this.queryResults.time_comparison.date_range.from_date),
              getCalendarDate(this.queryResults.time_comparison.date_range.to_date),
            ],
          },
        ];
      }

      draft.sections.group = viewReplaySections.group;
    });

    let recordingPlayerSource: RecordingPlayerSource;
    if (showClauses[0].behavior.type === MetricType.Funnel) {
      recordingPlayerSource = RecordingPlayerSource.Funnels;

      let replayStepIndex = stepIndex ?? showClauses[0].behavior.behaviors.length - 1;
      // for dropoff analysis, we want to see replays for users who converted through the previous step
      if (isDropOff) {
        replayStepIndex -= 1;
      }

      const isEventComparison = hasEventComparison([showClauses[0]]);
      const segmentFilters = this.getFiltersFromPopoverSegments(segment.slice(isEventComparison ? 1 : 0));

      // make sure that we break down on the selected step (will expand it by replay_id)
      // and filter for replay_id being set on the selected segment
      const funnelShowClause = produce(showClauses[0].serialize() as BehaviorMetric, (draft) => {
        const previousActionStep = draft.measurement.actionStep;

        // segment by replayID on the target step
        draft.measurement.actionMode = `step`;
        draft.measurement.actionStep = replayStepIndex + 1; // for some reason this is 1-indexed

        // for event comparisons, only resolve the steps we care about
        if (isEventComparison) {
          const steps = resolveCompareStepsQueryToConcreteFunnelMultiMetric(draft.behavior.behaviors as SubBehavior[], [
            segment[1],
          ]);
          draft.behavior.behaviors = steps;
        }

        // add step filters for the view replays query
        const stepFilters = draft.behavior.behaviors[replayStepIndex].filters;
        draft.behavior.behaviors[replayStepIndex].filters = stepFilters.concat(viewReplaySections.filter);

        // convert global filter / breakdowns into an inline filter on the previous actionStep
        if (previousActionStep && previousActionStep !== draft.measurement.actionStep) {
          const previousActionStepFilters = draft.behavior.behaviors[previousActionStep - 1].filters;
          draft.behavior.behaviors[previousActionStep - 1].filters = [
            ...previousActionStepFilters,
            ...bookmarkWithReplaySegmentation.sections.filter,
            ...segmentFilters,
          ];
        }
      });

      bookmarkWithReplaySegmentation = produce(bookmarkWithReplaySegmentation, (draft) => {
        draft.sections.show = [funnelShowClause];

        // global filters converted onto the show clause in previous step
        draft.sections.filter = [];

        // for dropoffs, we'll view replays for the previous step but explicitly filter
        // for users who have not completed the funnel (leveraging 'view users' logic).
        if (isDropOff) {
          const cohort = this.getFunnelCohort({segment, isDropOff, stepIndex});

          draft.sections.filter.push(makeCohortFilter(cohort));
        }
      });
    } else if ([MetricType.Event, MetricType.CustomEvent].includes(showClauses[0].behavior.type)) {
      recordingPlayerSource = RecordingPlayerSource.Event;

      bookmarkWithReplaySegmentation = produce(bookmarkWithReplaySegmentation, (draft) => {
        const showClause = {...showClauses[0].serialize()};
        showClause.measurement = MAX_BY_TIME_MEASUREMENT;

        draft.sections.show = [showClause];

        // apply filters for the selected segment
        draft.sections.filter = [
          ...draft.sections.filter,
          ...viewReplaySections.filter,
          ...this.getFiltersFromPopoverSegments(segment),
        ];
      });
    } else if ([MetricType.People].includes(showClauses[0].behavior.type)) {
      recordingPlayerSource = RecordingPlayerSource.InsightsProfile;

      const cohort = createNestedCohort(
        showClauses.map((clause, index) =>
          this.getCohortForSegmentShowClause(clause, segment, {
            index,
            subDayFilters,
            dateRange,
            forTimeComparisonSegment: isTimeComparisonSegment,
          }),
        ),
      );

      const cohortFilter = makeCohortFilter(cohort);

      bookmarkWithReplaySegmentation = produce(bookmarkWithReplaySegmentation, (draft) => {
        draft.sections.show = [LATEST_REPLAYS_SHOW_CLAUSE];
        draft.sections.filter = [cohortFilter, ...viewReplaySections.filter];
      });
    } else {
      // should never hit this case
      throw new Error(`Viewing replays is not supported for this bookmark.`);
    }

    const eventsAndReplaysPromise = queryUtils
      .makeSessionReplayQuery({
        params: bookmarkWithReplaySegmentation,
        blockedReplayFrames: getSessionRecordingBlockingInfo(this.getContext(`currentProject`).get().value),
        // over-fetch in case an events are tagged with a replay ID but the recording data didn't make it to our servers
        limit: DEFAULT_NUM_REPLAYS * 2,
      })
      .then(async (results) => {
        const targetEventMapping: RecordingEvents[] = [];
        const distinctIdsWithReplays: string[][] = [];

        flattenSeriesToDepth(results.series, bookmarkWithReplaySegmentation.sections.group.length).forEach(
          ([segments, values], idx) => {
            const segVals = queryUtils.getLabeledSegmentValues(segments, viewReplaySections.group);
            const time = Object.values(values).find((v) => v !== 0);

            if ([MetricType.Event, MetricType.CustomEvent].includes(showClauses[0].behavior.type)) {
              const existingEntry = targetEventMapping.find((entry) => entry.replayId === segVals.$mp_replay_id);
              const queriedEventItem: RecordingEventMetadata = {
                eventName: showClauses[0].behavior.name,
                eventTime: Number(time),
                quantity: 1,
                hasMatchingReplayIdProperty: isTrackedReplayId(segVals[REPLAY_ID_PROPERTY]),
                key: `queriedViewReplayEvent-${segVals[REPLAY_ID_PROPERTY]}-${idx}`,
              };

              if (existingEntry) {
                existingEntry.replayEvents.push(queriedEventItem);
              } else {
                targetEventMapping.push({
                  replayId: segVals.$mp_replay_id,
                  replayEvents: [queriedEventItem],
                });
              }
            }

            distinctIdsWithReplays.push([segVals.$distinct_id, segVals.$mp_replay_id]);
          },
        );
        const [distinctIds, replayIds] = unzip(distinctIdsWithReplays);
        return {
          replays: await sessionRecordingQueryUtils.fetchRecordingsByIds({
            replayIds: uniq(replayIds),
            distinctIds: uniq(distinctIds),
            blockedReplayFrames: getSessionRecordingBlockingInfo(this.getContext(`currentProject`).get().value),
            i18n: this.getContext(`i18n`),
            checkForStitchedReplays: true,
          }),
          events: targetEventMapping,
        };
      });

    const displayOptions = this.helpers.getDisplayOptions();
    RecordingPlayerModalAsync({
      eventsAndReplaysPromise,
      replayDateRange: dateRange,
      localStorageManager: this.getContext(`localStorageManager`),
      source: recordingPlayerSource,
      extraTrackingProps: {
        'Chart Type': displayOptions.chartType,
        'Plot Style': displayOptions.plotStyle,
        'Time Unit': displayOptions.timeUnit,
      },
    });
  }

  getCohortForSegmentShowClause(
    showClause: BehaviorShowClause,
    segment: string[],
    {index = null, subDayFilters = null, dateRange = null, forTimeComparisonSegment = null} = {},
  ) {
    const entries = getEntries({
      showClauses: [showClause],
      dataGroups: this.dataGroups,
      dateUnit: this.state.report.sections.time.clauses[0].unit,
      reportSectionsMetadata: this.queryResults.meta.report_sections,
    });
    const entry = entries[0] as Exclude<(typeof entries)[0], QueryFormulaEntry>;
    let frequency = null;

    // for top events, replace the selected top event event value
    let eventValue = ``;
    if (isMetricEntry(entry)) {
      eventValue = entry.behavior.name;
    } else if (!isSavedMetricEntry(entry)) {
      eventValue = entry.event.value;
    }
    if (eventValue === SPECIAL_EVENTS.TOP_EVENTS) {
      const parsedEventName = segment[0].slice(0, segment[0].lastIndexOf(` [`));
      if (isMetricEntry(entry)) {
        entry.behavior.name = findEventDefinitionByDisplayName(parsedEventName)?.name ?? parsedEventName;
        // TODO: MetricEntry needs label field as well
      } else if (!isSavedMetricEntry(entry)) {
        entry.event.value = findEventDefinitionByDisplayName(parsedEventName)?.name ?? parsedEventName;
        entry.event.label = parsedEventName;
      }
    }

    const mathType = isMetricEntry(entry) ? entry.measurement.aggregationOperator : entry.aggregationOperator;
    if (mathType === PRIMITIVE_MATH_TYPES.HISTOGRAM) {
      const lastSegment = segment[segment.length - 1];
      frequency = new FilterClause(createNumberFilter(null, {}, lastSegment, true)).toPropertyFilter();
    }

    const timeComparisonDateRange = this.queryResults.time_comparison?.date_range;
    // handle compare to past
    if (!dateRange && timeComparisonDateRange && forTimeComparisonSegment) {
      const {from_date: from, to_date: to} = timeComparisonDateRange;
      dateRange = new DateRange({
        type: DateRangeType.Between,
        from: getCalendarDate(from),
        to: getCalendarDate(to),
      });
    }

    // bar being clicked, transform to absolute date range
    if (!dateRange && !forTimeComparisonSegment) {
      dateRange = new DateRange({
        type: DateRangeType.Between,
        from: getCalendarDate(this.queryResults.date_range.from_date),
        to: getCalendarDate(this.queryResults.date_range.to_date),
      });
    }

    const nthTimeInPeriodFilter: PropertyFilter = getNthTimeInPeriodFilter(showClause);

    const globalFilterClauses: FilterClause[] = this.state.report.sections.filter.clauses
      .filter((filter) => !filter.isHidden)
      .map((filter) => filter.mapForAttribution(showClause))
      .map((filter) =>
        mapForScdAndRevenue(filter, {
          dateRange,
        }),
      );
    // for numerical breakdowns, ranges in the form of A - B, are exclusive on B so we must transform this filter
    // from between to an exclusive on the right filter
    const segmentFilterClauses: FilterClause[] = this.getFiltersFromPopoverSegments(segment, {
      makeNumericRangesExclusive: true,
    })
      .map((filter) => new FilterClause(filter).mapForAttribution(showClause))
      .map((filter) =>
        mapForScdAndRevenue(filter, {
          dateRange,
        }),
      );

    const filters = sortFiltersForCohort(
      subDayFilters as PropertyFilter[],
      nthTimeInPeriodFilter,
      segmentFilterClauses,
      globalFilterClauses,
    );

    return translateMetricToCohort(entry, {
      name: `${getAlphaLabelFromIndex(index)} - ${renameEvent(showClause.behavior.name)}`,
      dateRange,
      filters,
      keepUserAggregation: mathType === PRIMITIVE_MATH_TYPES.HISTOGRAM,
      ...(frequency
        ? {
            frequency,
            frequencyExclusive: true,
          }
        : {}),
    });
  }

  handleLineChartViewEvents(pointData: RenderLineChartTooltipParams) {
    const path = [pointData.metricClauseData.metricKey, ...pointData.segments];
    const dateRange = this.getLineChartViewEntitiesDateRange(pointData);

    this.handleViewEventsForSegment(path, {
      dateRange,
    });
  }

  handleViewEventsForSegment(segment: string[], {dateRange = null} = {}) {
    const behaviorShowClauses = this.getBehaviorClausesFromSegmentPath(segment);
    const globalFilters = this.state.report.sections.filter.clauses
      .map((filterClause) => filterClause.toPropertyFilter())
      .filter((filter) => !filter.isHidden);
    const filtersFromSegments = this.getFiltersFromPopoverSegments(segment).map((filter) =>
      new FilterClause(filter).toPropertyFilter(),
    );
    const timeClause = this.state.report.sections.time.clauses[0];

    this.track(ProdTrackingEvent.PopoverViewEvents, this.toTrackingData());

    const url = getEventsReportUrl({
      projectId: this.helpers.getProjectId(),
      workspaceId: this.helpers.getWorkspaceId(),
      entries: behaviorShowClauses ? convertSegmentForEventsInspector(behaviorShowClauses) : [],
      dateRange: dateRange || getDateRange([timeClause]),
      filters: [].concat(globalFilters, filtersFromSegments).filter(Boolean),
    });
    if (this.helpers.shouldOpenSidebar()) {
      openEventsSidebar({url});
    } else {
      window.open(url, `_blank`);
    }
  }

  hasMultipleListOfObjectsBreakdowns() {
    const groupClauses: GroupClause[] = this.state.report.sections.group.clauses;

    const numListOfObjects = groupClauses.filter((clause) => !!clause.listItemGroup).length;

    return numListOfObjects > 1;
  }

  handleViewInFlows({stepIndex}: {stepIndex: number}) {
    const detail = {
      report: BookmarkType.Flows,
      newUrlPath: getReportUrl(`flows`, {
        projectId: this.helpers.getProjectId(),
        workspaceId: this.helpers.getWorkspaceId(),
      }),
      additionalContext: {viewAsFlowsContext: {funnelStep: stepIndex - 1}},
    };
    const event: ReportSwitchEvent = new CustomEvent(`reportswitch`, {detail, composed: true, bubbles: true});
    this.track(ProdTrackingEvent.ViewAsFlowClicked);
    this.dispatchEvent(event);
  }

  dispatchUpdate(delta: Partial<InsightsBookmarkParams>) {
    const event: UpdateEvent = new CustomEvent(`update`, {detail: {delta}});
    this.dispatchEvent(event);
  }

  /**
   * When a funnels behavior has a filter added to it the show section must have 'actionMode' and
   * 'actionStep' set within the measurement clause of the show section. When a filter is added
   * via zooming in on a line chart these values must be updated here, if they are not already
   * present, since no QB update code is run through this path.
   *
   * @returns {ShowSection} The new show section with the updated 'actionMode' and 'actionStep'
   *                        fields for funnels.
   */
  getShowSectionForFunnelsFilters(): ShowSection {
    const showClause = this.state.report.sections.show.attrs.clauses[0];
    const showSection = ShowSection.createShowSection({
      behavior: showClause[`behavior`],
      measurement: showClause[`measurement`] as BehaviorShowClause[`measurement`],
      isExpanded: showClause[`isExpanded`],
    });
    showSection.getBehaviorClauses().forEach((clause) => {
      if (clause.behavior.type === MetricType.Funnel) {
        const {actionMode, actionStep} = clause.measurement;
        const defaultActionMode = `step`;
        const defaultActionStep = 1;
        clause.measurement.actionMode = actionMode || defaultActionMode;
        clause.measurement.actionStep = actionStep || defaultActionStep;
      }
    });
    return showSection;
  }
}

export const InsightsChartDisplayEl = defineAndMakeJsxFactory<ChartDisplayJsxProps>(
  `insights-chart-display`,
  ChartDisplay,
);
export const InsightsChartDisplayReact = panel2react<ChartDisplayJsxProps>(`insights-chart-display`, ChartDisplay);
